import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import axios from 'axios';
import { toast } from 'sonner';
import { TrendingUp, FileText, Package } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = () => {
  const { user, token, loading: authLoading } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [financialData, setFinancialData] = useState({
    monthly_income: '',
    monthly_expenses: '',
    current_savings: '',
    target_freedom_number: ''
  });
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    } else if (user) {
      loadDashboardData();
    }
  }, [user, authLoading]);

  const loadDashboardData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };

      // Load financial data
      const finResponse = await axios.get(`${API}/dashboard/financial`, { headers });
      if (finResponse.data) {
        setFinancialData(finResponse.data);
      }

      // Load notes
      const notesResponse = await axios.get(`${API}/dashboard/notes`, { headers });
      if (notesResponse.data) {
        setNotes(notesResponse.data.notes || '');
      }
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    }
  };

  const handleFinancialUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(
        `${API}/dashboard/financial`,
        {
          monthly_income: parseFloat(financialData.monthly_income),
          monthly_expenses: parseFloat(financialData.monthly_expenses),
          current_savings: parseFloat(financialData.current_savings),
          target_freedom_number: parseFloat(financialData.target_freedom_number)
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(t('language') === 'ro' ? 'Date actualizate!' : 'Data updated!');
    } catch (error) {
      toast.error('Failed to update data');
    } finally {
      setLoading(false);
    }
  };

  const handleNotesUpdate = async () => {
    setLoading(true);
    try {
      await axios.post(
        `${API}/dashboard/notes`,
        { notes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(t('language') === 'ro' ? 'Note salvate!' : 'Notes saved!');
    } catch (error) {
      toast.error('Failed to save notes');
    } finally {
      setLoading(false);
    }
  };

  const calculateSnapshot = () => {
    const income = parseFloat(financialData.monthly_income) || 0;
    const expenses = parseFloat(financialData.monthly_expenses) || 0;
    const savings = parseFloat(financialData.current_savings) || 0;
    const target = parseFloat(financialData.target_freedom_number) || 0;

    const monthlySavings = income - expenses;
    const savingsRate = income > 0 ? ((monthlySavings / income) * 100).toFixed(2) : 0;
    const monthsToTarget = monthlySavings > 0 ? Math.ceil((target - savings) / monthlySavings) : 0;

    return { savingsRate, monthlySavings, monthsToTarget };
  };

  const snapshot = calculateSnapshot();

  if (authLoading) {
    return <div className="flex min-h-screen items-center justify-center">Loading...</div>;
  }

  return (
    <div className="page-container min-h-screen py-16 bg-gradient-to-br from-mint-50 via-blue-50 to-white">
      <div className="container mx-auto px-4">
        <div className="mb-12">
          <h1 className="mb-2 text-4xl font-bold text-slate-900" data-testid="dashboard-title">
            {t('dashboard.title')}
          </h1>
          <p className="text-lg text-slate-600">
            Welcome back, {user?.full_name || user?.email}!
          </p>
        </div>

        <div className="grid gap-8 lg:grid-cols-3">
          {/* Left Column - Financial Snapshot & Data */}
          <div className="space-y-8 lg:col-span-2">
            {/* Financial Snapshot */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <TrendingUp className="mr-2 h-6 w-6 text-teal-600" />
                  {t('dashboard.financialSnapshot')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-6 md:grid-cols-3">
                  <div className="rounded-lg bg-gradient-to-br from-teal-50 to-slate-50 p-6">
                    <div className="mb-2 text-sm font-medium text-slate-600">Savings Rate</div>
                    <div className="text-3xl font-bold text-teal-600">{snapshot.savingsRate}%</div>
                  </div>
                  <div className="rounded-lg bg-gradient-to-br from-slate-50 to-teal-50 p-6">
                    <div className="mb-2 text-sm font-medium text-slate-600">Monthly Savings</div>
                    <div className="text-3xl font-bold text-slate-900">€{snapshot.monthlySavings}</div>
                  </div>
                  <div className="rounded-lg bg-gradient-to-br from-teal-50 to-slate-50 p-6">
                    <div className="mb-2 text-sm font-medium text-slate-600">Months to Target</div>
                    <div className="text-3xl font-bold text-teal-600">
                      {snapshot.monthsToTarget > 0 ? snapshot.monthsToTarget : '-'}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Financial Data Form */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle>Financial Data</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleFinancialUpdate} className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <Label htmlFor="income">Monthly Income (€)</Label>
                      <Input
                        id="income"
                        type="number"
                        step="0.01"
                        value={financialData.monthly_income}
                        onChange={(e) =>
                          setFinancialData({ ...financialData, monthly_income: e.target.value })
                        }
                        data-testid="income-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="expenses">Monthly Expenses (€)</Label>
                      <Input
                        id="expenses"
                        type="number"
                        step="0.01"
                        value={financialData.monthly_expenses}
                        onChange={(e) =>
                          setFinancialData({ ...financialData, monthly_expenses: e.target.value })
                        }
                        data-testid="expenses-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="savings">Current Savings (€)</Label>
                      <Input
                        id="savings"
                        type="number"
                        step="0.01"
                        value={financialData.current_savings}
                        onChange={(e) =>
                          setFinancialData({ ...financialData, current_savings: e.target.value })
                        }
                        data-testid="savings-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="target">Target Freedom Number (€)</Label>
                      <Input
                        id="target"
                        type="number"
                        step="0.01"
                        value={financialData.target_freedom_number}
                        onChange={(e) =>
                          setFinancialData({ ...financialData, target_freedom_number: e.target.value })
                        }
                        data-testid="target-input"
                      />
                    </div>
                  </div>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="bg-teal-600 hover:bg-teal-700"
                    data-testid="update-financial-button"
                  >
                    {t('dashboard.updateData')}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Resources & Notes */}
          <div className="space-y-8">
            {/* My Resources */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Package className="mr-2 h-5 w-5 text-teal-600" />
                  {t('dashboard.myResources')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 text-sm">
                  <div className="rounded-lg border border-slate-200 p-3">
                    <div className="font-medium text-slate-900">Financial Freedom Starter Pack</div>
                    <Button
                      variant="link"
                      className="h-auto p-0 text-teal-600"
                      onClick={() => navigate('/products/financial-freedom-starter-pack')}
                    >
                      View →
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* My Notes */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <FileText className="mr-2 h-5 w-5 text-teal-600" />
                  {t('dashboard.myNotes')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Write your financial notes here..."
                  className="min-h-[200px]"
                  data-testid="notes-textarea"
                />
                <Button
                  onClick={handleNotesUpdate}
                  disabled={loading}
                  className="mt-4 w-full bg-teal-600 hover:bg-teal-700"
                  data-testid="save-notes-button"
                >
                  {t('dashboard.saveNotes')}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;